/**
 * Hardware library (spec 37, 38).
 *
 * Searchable, faceted browser over the whole registry. Everything here goes
 * through ProfileRegistry.query(), which is indexed rather than linear, so the
 * same component works unchanged at 300 profiles and at 30,000.
 */

import { useDeferredValue, useMemo, useState } from 'react';
import { registry } from '../../database';
import type { AnyProfile, CableType, HardwareCategory } from '../../types/hardware';
import { CABLE_TYPE_LABEL, CABLE_TYPE_RATIONALE } from '../../types/hardware';
import type { Confidence } from '../../types/provenance';
import { CONFIDENCE_LABEL } from '../../types/provenance';
import type { SortKey } from '../../database/registry';
import type { CableSlotId, SlotId } from '../../types/system';
import { CABLE_SLOTS, CABLE_SLOT_LABEL, CABLE_SLOT_TYPE } from '../../types/system';
import { useApp } from '../../stores/appStore';
import {
  ConfidenceBadge,
  ConfidenceBar,
  HardwareArt,
  PRICE_STATUS_LABEL,
  Segmented,
  fmtPrice,
} from './primitives';

const CATEGORY_LABEL: Record<HardwareCategory, string> = {
  dac: 'DAC',
  amp: 'Amplifier',
  speaker: 'Speaker',
  headphone: 'Headphone',
  cable: 'Cable',
};

export function HardwareLibrary() {
  const slot = useApp((s) => s.librarySlot);
  const system = useApp((s) => s.librarySystem);
  const closeLibrary = useApp((s) => s.closeLibrary);
  const setProfile = useApp((s) => s.setProfile);
  const profileFor = useApp((s) => s.profileFor);
  const favourites = useApp((s) => s.favourites);
  const toggleFavourite = useApp((s) => s.toggleFavourite);
  const setBank = useApp((s) => s.setBank);
  const setCable = useApp((s) => s.setCable);
  const cableFor = useApp((s) => s.cableFor);

  /*
   * Cable slots and component slots share this browser.
   *
   * A cable slot always resolves to the single cable category, filtered down to
   * the one cableType that physically fits -- an Ethernet lead cannot be offered
   * for the speaker terminals -- so the filter is a `where` predicate on the
   * registry query rather than a separate component.
   */
  const cableSlot = slot && (CABLE_SLOTS as string[]).includes(slot) ? (slot as CableSlotId) : null;
  const wantedCableType: CableType | null = cableSlot ? CABLE_SLOT_TYPE[cableSlot] : null;
  const categories: HardwareCategory[] = cableSlot
    ? ['cable']
    : slot === 'output'
      ? ['speaker', 'headphone']
      : slot
        ? [slot as HardwareCategory]
        : ['dac'];
  const [tab, setTab] = useState<HardwareCategory>(categories[0]);
  const [text, setText] = useState('');
  const [brand, setBrand] = useState('');
  const [sort, setSort] = useState<SortKey>('manufacturer');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [conf, setConf] = useState<Confidence | ''>('');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(0);
  const [favOnly, setFavOnly] = useState(false);
  const [tag, setTag] = useState('');

  const deferredText = useDeferredValue(text);
  const current = cableSlot ? cableFor(system, cableSlot) : slot ? profileFor(system, slot as SlotId) : null;

  const result = useMemo(
    () =>
      registry.query({
        category: tab,
        text: deferredText,
        brands: brand ? [brand] : undefined,
        confidence: conf ? [conf] : undefined,
        priceMin: priceMin || undefined,
        priceMax: priceMax || undefined,
        tags: tag ? [tag] : undefined,
        favouritesOnly: favOnly,
        favourites,
        where: wantedCableType ? (p) => p.category === 'cable' && p.cableType === wantedCableType : undefined,
        sort,
        sortDir: dir,
        limit: 600,
      }),
    [tab, deferredText, brand, conf, priceMin, priceMax, tag, favOnly, favourites, sort, dir],
  );

  if (!slot) return null;

  const choose = (p: AnyProfile) => {
    if (cableSlot) setCable(system, cableSlot, p.id);
    else setProfile(system, slot as SlotId, p.id);
    closeLibrary();
  };

  return (
    <div className="library-overlay" onClick={closeLibrary} role="dialog" aria-modal="true" aria-label="Hardware library">
      <div className="library" onClick={(e) => e.stopPropagation()}>
        <div className="panel-head">
          <span>
            {cableSlot ? `Cable library - ${CABLE_SLOT_LABEL[cableSlot]}` : `Hardware library - ${slot.toUpperCase()} slot`}, System {system}
          </span>
          <div className="spacer" />
          <span className="faint tiny">
            {result.total} of {registry.countByCategory(tab)} {CATEGORY_LABEL[tab]}s
          </span>
          <button className="btn sm" onClick={closeLibrary}>
            Close <span className="kbd">Esc</span>
          </button>
        </div>

        <div className="library-filters">
          {categories.length > 1 && (
            <Segmented
              value={tab}
              onChange={(v) => {
                setTab(v);
                setBrand('');
                setTag('');
              }}
              options={categories.map((c) => ({ value: c, label: `${CATEGORY_LABEL[c]} (${registry.countByCategory(c)})` }))}
              ariaLabel="Category"
            />
          )}
          <input
            type="search"
            placeholder="Search manufacturer, model, chipset, topology, tags..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ flex: '1 1 240px', minWidth: 180 }}
            autoFocus
          />
          <select value={brand} onChange={(e) => setBrand(e.target.value)} style={{ width: 170 }} aria-label="Brand">
            <option value="">All brands</option>
            {result.facets.brands.map((b) => (
              <option key={b.value} value={b.value}>
                {b.value} ({b.count})
              </option>
            ))}
          </select>
          <select value={tag} onChange={(e) => setTag(e.target.value)} style={{ width: 160 }} aria-label="Technology">
            <option value="">All technologies</option>
            {result.facets.tags.slice(0, 40).map((t) => (
              <option key={t.value} value={t.value}>
                {t.value} ({t.count})
              </option>
            ))}
          </select>
          <select value={conf} onChange={(e) => setConf(e.target.value as Confidence | '')} style={{ width: 150 }} aria-label="Confidence">
            <option value="">Any confidence</option>
            {result.facets.confidence.map((c) => (
              <option key={c.value} value={c.value}>
                {CONFIDENCE_LABEL[c.value]} ({c.count})
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Min $"
            value={priceMin || ''}
            onChange={(e) => setPriceMin(Number(e.target.value) || 0)}
            style={{ width: 84 }}
            aria-label="Minimum price"
          />
          <input
            type="number"
            placeholder="Max $"
            value={priceMax || ''}
            onChange={(e) => setPriceMax(Number(e.target.value) || 0)}
            style={{ width: 84 }}
            aria-label="Maximum price"
          />
          <select
            value={`${sort}:${dir}`}
            onChange={(e) => {
              const [s, d] = e.target.value.split(':');
              setSort(s as SortKey);
              setDir(d as 'asc' | 'desc');
            }}
            style={{ width: 150 }}
            aria-label="Sort"
          >
            <option value="manufacturer:asc">Manufacturer A-Z</option>
            <option value="model:asc">Model A-Z</option>
            <option value="price:asc">Price, low to high</option>
            <option value="price:desc">Price, high to low</option>
            <option value="year:desc">Newest first</option>
            <option value="confidence:desc">Best data first</option>
          </select>
          <button className={`btn sm${favOnly ? ' on' : ''}`} onClick={() => setFavOnly((f) => !f)}>
            ★ Favourites
          </button>
        </div>

        <div className="library-grid">
          {result.items.map((p) => (
            <HardwareCard
              key={p.id}
              profile={p}
              current={current?.id === p.id}
              favourite={favourites.has(p.id)}
              onChoose={() => choose(p)}
              onFavourite={() => toggleFavourite(p.id)}
              onBank={cableSlot ? undefined : (i) => setBank(slot as SlotId, i, p.id)}
            />
          ))}
          {result.items.length === 0 && (
            <div className="dim" style={{ gridColumn: '1 / -1', padding: 24, textAlign: 'center' }}>
              Nothing matches those filters.
            </div>
          )}
        </div>

        <div className="panel-head" style={{ borderTop: '1px solid var(--line)', borderBottom: 'none' }}>
          {cableSlot && (
            <button
              className="btn sm"
              onClick={() => {
                setCable(system, cableSlot, null);
                closeLibrary();
              }}
            >
              Use no cable
            </button>
          )}
          <span className="faint tiny">
            {wantedCableType
              ? CABLE_TYPE_RATIONALE[wantedCableType]
              : 'Every profile states where its data came from. Nothing here is a measurement of an individual unit unless it says so explicitly.'}
          </span>
        </div>
      </div>
    </div>
  );
}

function HardwareCard({
  profile,
  current,
  favourite,
  onChoose,
  onFavourite,
  onBank,
}: {
  profile: AnyProfile;
  current: boolean;
  favourite: boolean;
  onChoose: () => void;
  onFavourite: () => void;
  /** Absent for cable slots, which have no rapid-switch banks. */
  onBank?: (index: number) => void;
}) {
  const setInspector = useApp((s) => s.setInspector);
  const tech = techLine(profile);

  return (
    <div className={`hw-card${current ? ' current' : ''}`} onDoubleClick={onChoose}>
      <div className="art">
        <HardwareArt profile={profile} height={44} />
      </div>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mfr">{profile.manufacturer}</div>
          <div className="model">{profile.model}</div>
        </div>
        <button
          className={`fav${favourite ? ' on' : ''}`}
          onClick={onFavourite}
          title={favourite ? 'Remove from favourites' : 'Add to favourites'}
          aria-label="Favourite"
        >
          ★
        </button>
      </div>

      <div className="row">
        <span className="price">{fmtPrice(profile.price)}</span>
        <span className="faint tiny">{PRICE_STATUS_LABEL[profile.price.status]}</span>
      </div>

      <div className="tiny dim" style={{ lineHeight: 1.35 }}>{tech}</div>

      <ConfidenceBar profile={profile} />
      <div className="row">
        <ConfidenceBadge value={profile.confidence} />
        {profile.year && <span className="faint tiny">{profile.year}</span>}
      </div>

      <div className="tags">
        {(profile.tags ?? []).slice(0, 3).map((t) => (
          <span className="tag" key={t}>
            {t}
          </span>
        ))}
      </div>

      <div className="actions">
        <button className="btn sm primary" onClick={onChoose} style={{ flex: 1 }}>
          Select
        </button>
        <button
          className="btn sm"
          onClick={() => setInspector('library-detail:' + profile.id)}
          title="Show the full profile, including provenance"
        >
          Detail
        </button>
        {onBank && (
        <select
          className="btn sm"
          style={{ width: 54, padding: '0 4px' }}
          value=""
          onChange={(e) => {
            if (e.target.value) onBank(Number(e.target.value));
            e.target.value = '';
          }}
          title="Assign to a rapid-switch bank (keys 1-4)"
          aria-label="Assign to bank"
        >
          <option value="">Bank</option>
          <option value="0">1</option>
          <option value="1">2</option>
          <option value="2">3</option>
          <option value="3">4</option>
        </select>
        )}
      </div>
    </div>
  );
}

function techLine(p: AnyProfile): string {
  switch (p.category) {
    case 'dac':
      return `${p.conversion.architecture} · ${p.conversion.core} · ${p.analog.stage} output`;
    case 'amp':
      return `${p.topology} · ${p.form}${p.power.watts8 ? ` · ${p.power.watts8} W/8Ω` : p.power.outputVrms ? ` · ${p.power.outputVrms} Vrms` : p.power.mw300 ? ` · ${p.power.mw300} mW/300Ω` : ''}`;
    case 'speaker':
      return `${p.speakerType} · ${p.cabinet.enclosure} · ${p.sensitivityDb} dB · ${p.impedance.nominalOhms ?? '?'}Ω · ${p.cabinet.f3Hz ?? '?'} Hz`;
    case 'headphone':
      return `${p.headphoneType} · ${p.fit}${p.biasVolts ? ` · ${p.biasVolts} V bias` : p.impedanceOhms ? ` · ${p.impedanceOhms}Ω` : ''}`;
    case 'cable':
      return `${p.cableType} · ${p.conductor} · ${p.geometry}${p.gauge ? ` · ${p.gauge} AWG` : ''}`;
  }
}
